/**
 * Patient Service API Client
 *
 * Wraps all tms-api-patient endpoints (base URL configured via VITE_API_URL).
 * Every function returns the raw Axios response so callers can access headers
 * or status codes when needed.
 */
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type {
  ApiResponse,
  ApiPatientListItem,
  ApiPatientDetail,
  ApiAllergen,
  ApiAllergySeverity,
  ApiFacility,
  ApiCareUnit,
  ApiBed,
  ApiStaffUserInfo,
  ApiMotherSummary,
  ApiMilkProviderListItem,
  CreatePatientRequest,
  UpdatePatientRequest,
  ApiDesignatedDonorItem,
  ApiMedicalHistoryItem,
  ApiMilkLogItem,
  ApiMedicationItem,
  CreateMedicalHistoryRequest,
  UpdateMedicalHistoryRequest,
  CreateMilkLogRequest,
  CreateMedicationRequest,
  UpdateMedicationRequest,
  LinkMotherInfantRequest,
  DischargePatientRequest,
  ReadmitPatientRequest,
} from '../types';
import type {
  PatientFeedsResponse,
  PatientHistoryResponse,
  PatientHistoryItem,
  DischargePatientResponse,
  DischargePreviewResponse,
  ReadmitPatientResponse,
} from '@tms-phoenix/tms-types';

/** Derived feed-status union from the shared schema. */
type FeedStatus = PatientHistoryItem['status'];

const PATIENT_PREFIX = API_ROUTES.PATIENT;
const ALLERGENS_PREFIX = API_ROUTES.ALLERGENS;
const FACILITIES_PREFIX = API_ROUTES.FACILITIES;

/** Valid acuity-level values accepted by the patient list endpoint. */
export type AcuityLevel = 'STABLE' | 'MODERATE' | 'SERIOUS' | 'CRITICAL' | 'LIFE_THREATENING';

// --- Patients --------------------------------------------------------------

/** POST /api/v1/patients -- Create (admit) a new patient. */
export function createPatient(body: CreatePatientRequest) {
  const api = getApiClient();
  return api.post<ApiResponse<ApiPatientDetail>>(`${PATIENT_PREFIX}/patients`, body);
}

/** GET /api/v1/patients -- List patients with search, filters & pagination. */
export function fetchPatients(params: {
  page?: number;
  limit?: number;
  search?: string;
  /**
   * API acuity_level filter. Accepts a single value or an array — both are
   * normalised to an array before being sent so the server always receives a
   * consistent multi-value list.
   */
  status?: AcuityLevel | AcuityLevel[];
  staff_id?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** Filter by donor consent status. true = consented, false = not consented. Omit to return all. */
  donor_consent?: boolean;
  /** Filter by date of birth — from date (UTC ISO string). */
  from?: string;
  /** Filter by date of birth — to date (UTC ISO string). */
  to?: string;
  /** Filter by ADT (active/discharged) status. true = active (default), false = discharged, all = both. */
  is_active?: 'true' | 'false' | 'all';
}) {
  const api = getApiClient();
  const { status, ...rest } = params;
  const normalizedStatus = status === undefined
    ? undefined
    : Array.isArray(status) ? status : [status];
  return api.get<ApiResponse<ApiPatientListItem[]>>(`${PATIENT_PREFIX}/patients`, {
    params: { ...rest, ...(normalizedStatus !== undefined && { status: normalizedStatus }) },
  });
}


/** GET /api/v1/patients/:patient_id -- Get a single patient by ID. */
export function getPatientById(patientId: string) {
  const api = getApiClient();
  return api.get<ApiResponse<ApiPatientDetail>>(`${PATIENT_PREFIX}/patients/${patientId}`);
}

/** DELETE /api/v1/patients/:patient_id -- Discharge / delete a patient. */
export function deletePatient(patientId: string) {
  const api = getApiClient();
  return api.delete<ApiResponse<{ patient_id: string }>>(`${PATIENT_PREFIX}/patients/${patientId}`);
}

/** PATCH /api/v1/patients/:patient_id -- Partially update a patient. */
export function updatePatient(patientId: string, body: UpdatePatientRequest) {
  const api = getApiClient();
  return api.patch<ApiResponse<ApiPatientDetail>>(
    `${PATIENT_PREFIX}/patients/${patientId}`,
    body,
  );
}

/** GET /api/v1/patients/:patient_id/discharge-preview -- Preview prepared feeds that would be sent home on discharge. */
export function fetchDischargePreview(patientId: string) {
  const api = getApiClient();
  return api.get<ApiResponse<DischargePreviewResponse>>(
    `${PATIENT_PREFIX}/patients/${patientId}/discharge-preview`,
  );
}

/** POST /api/v1/patients/:patient_id/discharge -- Clinically discharge a patient. */
export function dischargePatient(patientId: string, body: DischargePatientRequest = {}) {
  const api = getApiClient();
  return api.post<ApiResponse<DischargePatientResponse>>(
    `${PATIENT_PREFIX}/patients/${patientId}/discharge`,
    body,
  );
}

/** POST /api/v1/patients/:patient_id/readmit -- Re-admit a previously discharged patient. */
export function readmitPatient(patientId: string, body: ReadmitPatientRequest = {}) {
  const api = getApiClient();
  return api.post<ApiResponse<ReadmitPatientResponse>>(
    `${PATIENT_PREFIX}/patients/${patientId}/readmit`,
    body,
  );
}

// --- Patient--Staff Assignments ---------------------------------------------

/** POST /api/v1/patientAssignment -- Bulk-assign patients to a staff member. */
export function assignPatientsToStaff(
  staffId: string,
  patients: {
    patientId: string;
    encounterId: string;
    hospitalId: string;
  }[],
) {
  const api = getApiClient();
  return api.post<
    ApiResponse<{ created: number; skipped: string[] }>
  >(`${PATIENT_PREFIX}/patientAssignment`, {
    staffId,
    patients,
  });
}

/** PATCH /api/v1/patientAssignment/:staff_id -- End (un-assign) patient assignments. */
export function unassignPatientsFromStaff(staffId: string, patientIds: string[]) {
  const api = getApiClient();
  return api.patch<
    ApiResponse<{ updated: number; notFound: string[] }>
  >(`${PATIENT_PREFIX}/patientAssignment/${staffId}`, { patientIds: patientIds });
}

/** GET /api/v1/patientAssignment/:patient_id -- Assigned staff for a patient. */
export function getStaffForPatient(patientId: string) {
  const api = getApiClient();
  return api.get<
    { patient_id: string; staff_count: number; staff: ApiStaffUserInfo[] }
  >(`${PATIENT_PREFIX}/patientAssignment/${patientId}`);
}

/** GET /api/v1/patient-staff/staff/:staffId/patient-count -- Workload count. */
export function getPatientCountForStaff(staffId: string) {
  const api = getApiClient();
  return api.get<
    { staffId: string; patientCount: number }
  >(`${PATIENT_PREFIX}/patient-staff/staff/${staffId}/patient-count`);
}

// --- Reference / Lookup ----------------------------------------------------

/** GET /api/v1/allergens */
export function fetchAllergens() {
  const api = getApiClient();
  return api.get<ApiAllergen[]>(`${ALLERGENS_PREFIX}/allergens`);
}

/** GET /api/v1/allergySeverity */
export function fetchAllergySeverities() {
  const api = getApiClient();
  return api.get<ApiAllergySeverity[]>(`${ALLERGENS_PREFIX}/allergySeverity`);
}

/** GET /api/v1/locations */
export function fetchLocations() {
  const api = getApiClient();
  return api.get<ApiFacility[]>(`${FACILITIES_PREFIX}/locations`);
}

/** GET /api/v1/careUnits?facilityId= */
export function fetchCareUnits(facilityId?: string) {
  const api = getApiClient();
  if (!facilityId) return api.get<ApiCareUnit[]>(`${FACILITIES_PREFIX}/careUnits`);
  return api.get<ApiCareUnit[]>(`${FACILITIES_PREFIX}/careUnits`, { params: { facilityId } });
}

/** GET /api/v1/beds/:unit_id */
export function fetchBeds(unitId: string) {
  const api = getApiClient();
  return api.get<ApiBed[]>(`${FACILITIES_PREFIX}/beds/${unitId}`);
}

/** GET /api/v1/milkProviders/mothers/?hospital_id= -- Slim list of mothers/donors for picker. */
export function fetchMothers() {
  const api = getApiClient();
  return api.get<ApiResponse<ApiMotherSummary[]>>(`${PATIENT_PREFIX}/milkProviders/mothers/`);
}

/**
 * GET /api/v1/milkProviders/list
 *
 * Returns a paginated list of milk providers for the given hospital.
 *
 * Query parameters:
 *  - `hospital_id` (required) — scopes results to the hospital
 *  - `role` (optional) — filters by provider role:
 *      `'MOTHER'`, `'DONOR'`, `'BOTH'`, `'MOTHER_AND_BOTH'`, `'DONOR_AND_BOTH'`
 *  - `page` / `limit` (optional) — pagination controls
 *
 * Used by donor/mother picker dialogs (e.g. Link Donor, designated-recipient
 * selectors) where only a specific role subset should be shown.
 */
export function fetchMilkProvidersList(params?: { role?: 'MOTHER' | 'DONOR' | 'BOTH' | 'MOTHER_AND_BOTH' | 'DONOR_AND_BOTH'; page?: number; limit?: number }) {
  const api = getApiClient();
  return api.get<ApiResponse<ApiMilkProviderListItem[]>>(`${PATIENT_PREFIX}/milkProviders/list`, {
    params,
  });
}

// --- Mother--Infant Links --------------------------------------------------

/** POST /api/v1/motherInfantLinks/link -- Link a donor/mother to a patient. */
export function linkMotherInfant(body: LinkMotherInfantRequest) {
  const api = getApiClient();
  return api.post<ApiResponse<{ link_id: string }>>(`${PATIENT_PREFIX}/motherInfantLinks/link`, body);
}

// --- Designated Donors ----------------------------------------------------

/**
 * GET /api/v1/patientDonorPreferences/patients/:patient_id/donors
 * Retrieve the prioritised list of designated milk donors for this patient.
 * Use priority_order ascending -- lower = higher priority.
 * Check screening_status before dispensing: FAILED or PENDING require clinical review.
 */
export function getDesignatedDonors(
  patientId: string,
  params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    /** Pass false to retrieve historical (deactivated) preferences. */
    is_active?: boolean;
  },
) {
  const api = getApiClient();
  return api.get<ApiResponse<ApiDesignatedDonorItem[]>>(
    `${PATIENT_PREFIX}/patientDonorPreferences/patients/${patientId}/donors`,
    { params },
  );
}

// --- Patient Profile -- Medical History ------------------------------------

/** GET /api/v1/patientProfile/patients/:patient_id?hospital_id= -- Load the latest medical history narrative. */
export function getMedicalHistory(patientId: string) {
  const api = getApiClient();
  return api.get<ApiResponse<ApiMedicalHistoryItem | null>>(
    `${PATIENT_PREFIX}/patientProfile/patients/${patientId}`,
  );
}

/** POST /api/v1/patientProfile/patients -- Add a new medical history record. */
export function createMedicalHistory(body: CreateMedicalHistoryRequest) {
  const api = getApiClient();
  return api.post<ApiResponse<ApiMedicalHistoryItem>>(
    `${PATIENT_PREFIX}/patientProfile/patients`,
    body,
  );
}

/** PATCH /api/v1/patientProfile/patients/:patient_id/:history_id?hospital_id= -- Update or deactivate a medical history record. */
export function updateMedicalHistory(
  patientId: string,
  historyId: string,
  body: UpdateMedicalHistoryRequest,
) {
  const api = getApiClient();
  return api.patch<ApiResponse<ApiMedicalHistoryItem>>(
    `${PATIENT_PREFIX}/patientProfile/patients/${patientId}/${historyId}`,
    body,
  );
}

// --- Patient Profile -- Feeding Plan (Milk Log) ----------------------------

/** GET /api/v1/patientProfileMilkLog/:patient_id?hospital_id= -- List feeding plans (paginated). */
export function getMilkLogs(
  patientId: string,
  params: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    /** Filter by active status: 'true' | 'false' */
    is_active?: 'true' | 'false';
  },
) {
  const api = getApiClient();
  return api.get<ApiResponse<ApiMilkLogItem[]>>(
    `${PATIENT_PREFIX}/patientProfileMilkLog/${patientId}`,
    { params },
  );
}

/** POST /api/v1/patientProfileMilkLog -- Create a new feeding plan entry. */
export function createMilkLog(body: CreateMilkLogRequest) {
  const api = getApiClient();
  return api.post<ApiResponse<ApiMilkLogItem>>(
    `${PATIENT_PREFIX}/patientProfileMilkLog`,
    body,
  );
}

// --- Patient Profile -- Current Medications --------------------------------

/** GET /api/v1/patientCurrentMedications/:patient_id?hospital_id= -- List current medications (paginated). */
export function getMedications(
  patientId: string,
  params: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    /** Filter by active status: 'true' | 'false' */
    is_active?: 'true' | 'false';
  },
) {
  const api = getApiClient();
  return api.get<ApiResponse<ApiMedicationItem[]>>(
    `${PATIENT_PREFIX}/patientCurrentMedications/${patientId}`,
    { params },
  );
}

/** POST /api/v1/patientCurrentMedications -- Add a new medication. */
export function createMedication(body: CreateMedicationRequest) {
  const api = getApiClient();
  return api.post<ApiResponse<ApiMedicationItem>>(
    `${PATIENT_PREFIX}/patientCurrentMedications`,
    body,
  );
}

/** PATCH /api/v1/patientCurrentMedications/:patient_id/:medication_id?hospital_id= -- Update or discontinue a medication. */
export function updateMedication(
  patientId: string,
  medicationId: string,
  body: UpdateMedicationRequest,
) {
  const api = getApiClient();
  return api.patch<ApiResponse<ApiMedicationItem>>(
    `${PATIENT_PREFIX}/patientCurrentMedications/${patientId}/${medicationId}`,
    body,
  );
}

// --- Patient Profile -- Feed Prep (patient-feeds & patient-history) --------

const FEED_MGMT_PREFIX = API_ROUTES.FEED_MANAGEMENT;

/** GET /api/v1/feedPrep/patient-feeds/:patient_id?hospital_id= -- List active/pending feeds for a patient (Feeding tab). */
export function getPatientFeeds(patientId: string) {
  const api = getApiClient();
  return api.get<ApiResponse<PatientFeedsResponse>>(
    `${FEED_MGMT_PREFIX}/feedPrep/patient-feeds/${patientId}`,
  );
}

/** GET /api/v1/feedPrep/patient-history/:patient_id?hospital_id=&status=&days= -- Feed history for a patient (History tab). */
export function getPatientFeedHistory(
  patientId: string,
  params?: {
    status?: FeedStatus;
    days?: number;
  },
) {
  const api = getApiClient();
  return api.get<ApiResponse<PatientHistoryResponse>>(
    `${FEED_MGMT_PREFIX}/feedPrep/patient-history/${patientId}`,
    { params },
  );
}
